'use client';

import { useTranslations } from 'next-intl';
import { Globe } from 'lucide-react';
import { Label } from '@/shared/components/ui/label';
import { CheckboxField } from '@/shared/components/ui/checkbox-field';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select';
import { locales } from '@/shared/i18n';

interface TranslateStepProps {
  sourceLanguage: string;
  targetLanguage: string;
  onTargetLanguageChange: (lang: string) => void;
  sourceStoryTitle?: string | null;
  /** What the source story already has — a drawn book, a rendered narration. */
  hasBook?: boolean;
  hasNarration?: boolean;
  textOnly?: boolean;
  onTextOnlyChange?: (v: boolean) => void;
}

export function TranslateStep({
  sourceLanguage,
  targetLanguage,
  onTargetLanguageChange,
  sourceStoryTitle,
  hasBook = false,
  hasNarration = false,
  textOnly = false,
  onTextOnlyChange,
}: TranslateStepProps) {
  const t = useTranslations('content.wizard');

  return (
    <div data-testid="content-wizard-translate-step" className="space-y-6">
      <div className="flex items-center gap-3">
        <div className="flex size-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
          <Globe className="size-5" />
        </div>
        <h3 className="text-base font-medium">{t('translateStep.title')}</h3>
      </div>

      <p className="text-sm text-muted-foreground" data-testid="content-translate-explanation">
        {t('translateStep.explanation')}
      </p>

      {sourceStoryTitle && (
        <div className="rounded-md border bg-muted/50 p-3">
          <span className="text-sm text-muted-foreground">{t('storySelect.selected')}: </span>
          <span className="text-sm font-medium">{sourceStoryTitle}</span>
        </div>
      )}

      {/* The translation is the same story, so it grows the same shapes — and
          the screen has to say which, because the parent is about to be billed
          for a book and a narration they did not explicitly order. Nothing
          there → nothing to say, and no choice to offer. */}
      {(hasBook || hasNarration) && (
        <div className="space-y-3 rounded-md border px-4 py-3" data-testid="content-translate-layers">
          <p className="text-sm text-muted-foreground">
            {hasBook && hasNarration
              ? t('translateStep.layersBoth')
              : hasBook
                ? t('translateStep.layersBook')
                : t('translateStep.layersAudio')}
          </p>
          <CheckboxField
            id="translate-text-only"
            checked={textOnly}
            onCheckedChange={(c) => onTextOnlyChange?.(c)}
            data-testid="content-translate-text-only"
            label={t('translateStep.textOnlyLabel')}
            hint={t('translateStep.textOnlyHint')}
          />
        </div>
      )}

      <div className="grid gap-3 sm:gap-4 sm:grid-cols-2">
        <div className="space-y-2">
          <Label>{t('translateStep.sourceLanguage')}</Label>
          <div className="flex h-10 items-center rounded-md border bg-muted/50 px-3 text-sm text-muted-foreground">
            {t(`languages.${sourceLanguage}`)}
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="target-language">{t('translateStep.targetLanguage')}</Label>
          <Select value={targetLanguage} onValueChange={onTargetLanguageChange}>
            <SelectTrigger id="target-language" data-testid="content-translate-target-language">
              <SelectValue placeholder={t('translateStep.selectLanguage')} />
            </SelectTrigger>
            <SelectContent>
              {locales.filter((lang) => lang !== sourceLanguage).map((lang) => (
                <SelectItem key={lang} value={lang} data-testid={`content-translate-target-${lang}`}>
                  {t(`languages.${lang}`)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
    </div>
  );
}
